// Central data store for all EzeitPro use-case pages + mega-menu
import { CAR_HIGHLIGHTS, CAR_SHOWCASE, shotBase } from './marketingModules';

export const USE_CASES = [
  {
    slug: 'healthcare',
    industry: 'Healthcare',
    tagline: 'Clinic & Doctor Appointment Management',
    services: ['Clinic Management', 'Doctor Appointments'],
    heroSubtitle:
      'Run a smarter clinic. Reduce no-shows, manage doctor schedules, and deliver a better patient experience — all from one platform.',
    challenge:
      'Running a clinic is far more than practising medicine. Appointment books fill up, doctors have varying availability, patients miss consultations, and administrative staff juggle phone calls, paper records, and billing simultaneously. Without a structured system, clinics face appointment conflicts, revenue leakage from no-shows, and staff burnout — all of which directly impact the quality of patient care.',
    solutions: [
      {
        icon: 'Calendar',
        title: 'Appointment & Scheduling Management',
        body: 'Patients book available slots online, via WhatsApp, or through walk-in registration — all flowing into a unified calendar. Doctor availability, consultation duration, and room or equipment allocation are configured once and respected automatically.',
      },
      {
        icon: 'Users',
        title: 'Doctor & Resource Management',
        body: 'Each doctor, nurse, or consultation room is treated as a bookable resource. EzeitPro prevents double-bookings, manages shift schedules, and gives clinic owners a real-time view of who is available, occupied, or on leave.',
      },
      {
        icon: 'MessageSquare',
        title: 'Automated Patient Communications',
        body: 'Appointment confirmations, reminders, and follow-up messages are sent automatically via SMS and WhatsApp — dramatically reducing no-shows without requiring staff to make individual reminder calls.',
      },
      {
        icon: 'FileText',
        title: 'Tax-Compliant Invoicing',
        body: 'Generate professional invoices per consultation or procedure, with support for UAE VAT and India GST. Patients receive digital invoices instantly, and payment records are maintained automatically.',
      },
      {
        icon: 'BarChart3',
        title: 'Analytics & Reporting',
        body: 'Track appointment volumes, peak consultation hours, revenue per doctor, and no-show rates. Use data to make informed decisions about staffing, opening hours, and service pricing.',
      },
    ],
    benefits: [
      'Eliminate appointment conflicts with real-time availability management',
      'Reduce no-shows by up to 30% with automated reminders',
      'Free administrative staff from manual scheduling and follow-up calls',
      'Maintain accurate billing records with compliant digital invoices',
      'Gain full visibility into clinic performance with built-in analytics',
    ],
  },
  {
    slug: 'home-maintenance',
    industry: 'Home Maintenance',
    tagline: 'Cleaning & Pest Control Services',
    services: ['Cleaning Services', 'Pest Control'],
    heroSubtitle:
      'Coordinate field crews, delight customers with timely service, and grow your home maintenance business — without the administrative chaos.',
    challenge:
      'Home maintenance businesses face a relentless operational juggle: scheduling multiple teams across different locations each day, ensuring the right crew with the right equipment reaches the right address on time, and collecting payment without delays. Manual coordination via phone calls and spreadsheets creates scheduling gaps, miscommunication, and lost revenue.',
    solutions: [
      {
        icon: 'Calendar',
        title: 'Booking & Scheduling Management',
        body: 'Customers book cleaning or pest control visits online, via WhatsApp, or through your team directly. Every booking lands in a central calendar with the customer\'s address, service type, and preferred time — ready to assign instantly.',
      },
      {
        icon: 'Users',
        title: 'Crew Management',
        body: 'Assign the right technician or cleaning crew to each job based on availability, location, or specialisation. Track who is where in real time from a single dashboard, and balance workloads across your entire field team.',
      },
      {
        icon: 'MessageSquare',
        title: 'Automated Customer Communications',
        body: 'Customers receive automatic booking confirmations and day-before reminders via SMS and WhatsApp — reducing last-minute cancellations and ensuring teams never arrive at empty addresses.',
      },
      {
        icon: 'FileText',
        title: 'Invoicing & Payments',
        body: 'Issue professional invoices immediately after job completion. With UAE VAT and India GST compliance built in, billing is accurate, fast, and professional. Collect payments digitally and maintain a clean financial record automatically.',
      },
      {
        icon: 'BarChart3',
        title: 'Analytics & Reporting',
        body: 'Monitor job completion rates, crew utilisation, repeat booking frequency, and revenue trends by service type or location. Identify your most profitable service lines and peak demand periods.',
      },
    ],
    benefits: [
      'Eliminate double-bookings and missed assignments with real-time crew scheduling',
      'Cut no-shows with automated reminder messages to customers',
      'Speed up payment collection with digital, tax-compliant invoicing',
      'Gain full operational visibility across multiple crews and locations',
      'Scale team capacity without proportionally increasing administrative effort',
    ],
  },
  {
    slug: 'home-improvements',
    industry: 'Home Improvements',
    tagline: 'Landscaping & Interior Design Services',
    services: ['Landscaping', 'Interior Design'],
    heroSubtitle:
      'Manage projects, coordinate skilled crews, and deliver exceptional client experiences — from the first site visit to the final invoice.',
    challenge:
      'Landscaping and interior design businesses operate on project timelines, not simple appointments. Coordinating site visits, design consultations, project crews, sub-contractors, and material deliveries — while managing client expectations — creates an operational complexity that spreadsheets cannot handle. Delays, miscommunication, and invoicing disputes erode both profitability and reputation.',
    solutions: [
      {
        icon: 'Calendar',
        title: 'Booking & Consultation Scheduling',
        body: 'Clients book initial site visits or design consultations directly through your booking link, WhatsApp, or walk-in. Each booking captures the job location, service type, and client notes — ensuring your team arrives fully informed.',
      },
      {
        icon: 'Users',
        title: 'Crew & Resource Management',
        body: 'Schedule the right crew for each project phase — landscaping teams, interior designers, painters, or specialist contractors. Manage availability, assign roles, and prevent scheduling conflicts across simultaneous projects.',
      },
      {
        icon: 'MessageSquare',
        title: 'Automated Client Communications',
        body: 'Send automatic appointment confirmations, visit reminders, and project milestone updates to clients via SMS and WhatsApp. Professional, timely communication builds client confidence and reduces unnecessary follow-up calls.',
      },
      {
        icon: 'FileText',
        title: 'Project Invoicing & Payments',
        body: 'Create detailed, itemised invoices covering labour, materials, and services — with UAE VAT and India GST compliance. Issue advance payment requests, milestone-based billing, or final settlement invoices digitally.',
      },
      {
        icon: 'BarChart3',
        title: 'Analytics & Business Insights',
        body: 'Track revenue by project type, monitor crew utilisation, identify your most in-demand services, and measure average project value. Make data-backed decisions about where to grow your business next.',
      },
    ],
    benefits: [
      'Coordinate multiple projects and crews without scheduling conflicts',
      'Deliver a professional client experience from consultation to final invoice',
      'Reduce administrative overhead and manual follow-ups significantly',
      'Maintain compliant, accurate billing records for every project',
      'Understand which services and clients drive the most revenue',
    ],
  },
  {
    slug: 'building-services',
    industry: 'Building Services',
    tagline: 'Window Cleaning & AC Services',
    services: ['Window Cleaning', 'AC Services'],
    heroSubtitle:
      'Manage recurring contracts, dispatch skilled technicians, and maintain flawless service records — without the scheduling headaches.',
    challenge:
      'Window cleaning and AC maintenance businesses depend on precise scheduling, recurring service agreements, and fast crew deployment. Managing repeat visit schedules for multiple commercial or residential clients, tracking service history per unit, handling AMC renewals, and issuing accurate invoices manually is time-consuming and error-prone. Missed visits and billing delays directly impact client retention and cash flow.',
    solutions: [
      {
        icon: 'Calendar',
        title: 'Booking & Recurring Schedule Management',
        body: 'Set up one-time or recurring service schedules for each client property. Whether it is monthly window cleaning or quarterly AC servicing, EzeitPro manages the schedule and automatically queues upcoming visits — so nothing falls through the cracks.',
      },
      {
        icon: 'Users',
        title: 'Crew & Technician Management',
        body: 'Assign the right technician to each job based on location, skill set, or certification. Track technician availability and workload across multiple sites, and manage shift rosters from a single dashboard.',
      },
      {
        icon: 'MessageSquare',
        title: 'Automated Service Reminders',
        body: 'Customers receive timely service reminders via SMS and WhatsApp before each scheduled visit — improving preparedness, reducing rescheduling requests, and strengthening client relationships.',
      },
      {
        icon: 'FileText',
        title: 'Service History & Invoicing',
        body: 'Maintain a per-unit or per-site service history for easy reference during audits or warranty claims. Generate professional, tax-compliant invoices (UAE VAT / India GST) upon job completion, and accept digital payments instantly.',
      },
      {
        icon: 'BarChart3',
        title: 'Analytics & Business Insights',
        body: 'Track service volume, crew productivity, revenue by client or service type, and AMC renewal rates. Identify operational bottlenecks and high-value accounts to prioritise for retention efforts.',
      },
    ],
    benefits: [
      'Never miss a recurring service visit with automated schedule management',
      'Deploy the right technician to every job with real-time crew visibility',
      'Reduce admin effort with automated communications and digital invoicing',
      'Maintain complete service histories per client property',
      'Grow revenue by identifying and prioritising high-value contracts',
    ],
  },
  {
    slug: 'facility-services',
    industry: 'Facility Services',
    tagline: 'Plumbing, Carpentry, Masonry & Building Repairs',
    services: ['Plumbing Services', 'Carpentry & Masonry', 'Building Repairs'],
    heroSubtitle:
      'Dispatch the right tradesperson to every job, track work across multiple sites, and get paid faster — with zero paperwork.',
    challenge:
      'Facility service businesses handle unpredictable demand. Emergency plumbing calls, repair requests, and scheduled maintenance jobs all compete for the same pool of skilled tradespeople. Without a system, dispatching the right technician quickly, tracking job status, and issuing accurate invoices across multiple concurrent jobs becomes chaotic. Missed jobs, unclear billing, and crew idle time eat directly into profitability.',
    solutions: [
      {
        icon: 'Calendar',
        title: 'Booking & Job Request Management',
        body: 'Accept service requests online, via WhatsApp, or walk-in. Each request captures the job type, location, urgency, and customer details — instantly queued for assignment. For emergency requests, jobs can be created and assigned in minutes.',
      },
      {
        icon: 'Users',
        title: 'Crew & Specialist Management',
        body: 'Match the right tradesperson to each job — a certified plumber for pipe repairs, a carpenter for joinery, a mason for structural work. Manage individual availability, track active assignments in real time, and balance workloads across your team.',
      },
      {
        icon: 'MessageSquare',
        title: 'Automated Job Notifications',
        body: 'Customers receive automatic booking confirmations and on-the-way notifications via SMS and WhatsApp. Your crew receives job details directly — reducing miscommunication and ensuring technicians arrive prepared.',
      },
      {
        icon: 'FileText',
        title: 'Invoicing & Payment Collection',
        body: 'Issue detailed, itemised invoices covering labour, parts, and call-out fees — with full UAE VAT and India GST compliance. Settle payments digitally on-site or send invoices remotely. Maintain a complete billing history per customer.',
      },
      {
        icon: 'BarChart3',
        title: 'Analytics & Operational Insights',
        body: 'Analyse job volumes by trade, average resolution time, technician utilisation rates, and revenue per job category. Identify demand patterns to plan staffing and stock levels more accurately.',
      },
    ],
    benefits: [
      'Respond to service requests faster with streamlined job assignment',
      'Deploy skilled tradespeople to the right job every time',
      'Reduce billing disputes with transparent, itemised digital invoices',
      'Track all active and completed jobs from a single dashboard',
      'Build recurring revenue through service agreements and repeat customers',
    ],
  },
  {
    slug: 'personal-care',
    industry: 'Personal Care',
    tagline: 'Salon & Spa Services',
    services: ['Salon', 'Spa'],
    heroSubtitle:
      'Fill your appointment calendar, delight every guest, and grow your personal care business — with an operations platform that works as beautifully as you do.',
    challenge:
      'Salons and spas run on appointments. But managing a high-frequency booking calendar across multiple stylists, therapists, and treatment rooms — while handling walk-ins, last-minute cancellations, and product-based billing — stretches front-desk capacity to its limits. Overbooking damages the customer experience. Underbooking wastes revenue-generating hours. Without visibility, it is almost impossible to know which services, staff, or time slots drive the most value.',
    solutions: [
      {
        icon: 'Calendar',
        title: 'Multi-Channel Booking',
        body: 'Customers book appointments online, via WhatsApp, Google Business Profile, or walk-in. All bookings land in a unified calendar — eliminating double-bookings and ensuring every slot is managed in real time.',
      },
      {
        icon: 'Users',
        title: 'Staff & Resource Scheduling',
        body: 'Assign appointments to specific stylists, therapists, or treatment rooms based on availability and specialisation. Each team member has a personal schedule view, and changes are reflected instantly across the system.',
      },
      {
        icon: 'MessageSquare',
        title: 'Automated Reminders',
        body: 'Dramatically reduce no-shows by sending automatic appointment reminders to customers via SMS and WhatsApp — typically 24 hours and 1 hour before their visit. Fewer no-shows means more predictable revenue.',
      },
      {
        icon: 'FileText',
        title: 'Invoicing & Retail Billing',
        body: 'Issue digital invoices that combine service charges and retail product sales in a single transaction. With UAE VAT and India GST compliance, billing is accurate and professional. Maintain a purchase history per customer.',
      },
      {
        icon: 'BarChart3',
        title: 'Analytics & Business Intelligence',
        body: 'Track booking rates by stylist or treatment, identify your highest-revenue services, monitor peak hours, and measure the impact of promotions. Make informed decisions about staffing, pricing, and service offerings.',
      },
    ],
    benefits: [
      'Fill your appointment calendar efficiently with multi-channel booking',
      'Reduce no-shows and last-minute cancellations with automated reminders',
      'Maximise team productivity by matching the right staff to each appointment',
      'Simplify billing with combined service and retail invoicing',
      'Understand which services and staff members drive the most revenue',
    ],
  },
  {
    slug: 'auto-services',
    industry: 'Auto Services',
    tagline: 'Car Service Centre Management',
    services: ['Car Service Centre'],
    seoTitle: 'Car Service Centre & Workshop Management Software | EzeitPro',
    seoDescription:
      'Run a car service centre or workshop on one system: repair orders, technician board, warranty claims, parts with PO and GRN, UAE VAT finance and a 24x7 WhatsApp AI assistant.',
    heroSubtitle:
      'One system for job cards, technicians, parts, warranty, VAT and the books, plus an AI assistant that answers your customers on WhatsApp day and night while your team is under the bonnet.',
    heroShot: {
      base: shotBase('dms-repair-orders'),
      alt: 'Repair orders list with job status, pay type and technician',
    },
    challenge:
      'A busy service centre runs on a dozen moving parts at once: walk-ins and bookings competing for the same bays, technicians waiting on parts, warranty claims stuck on missing paperwork, insurers and customers splitting one bill, and a supplier invoice that never matches what arrived. When this lives on whiteboards, WhatsApp threads and spreadsheets, jobs overrun, parts go missing, warranty money is left unclaimed, VAT returns take days to assemble, and customers who call after hours reach nobody.',
    solutions: [
      {
        icon: 'Calendar',
        title: 'Appointment & Service Booking',
        body: 'Customers book service slots online, via WhatsApp, or through your front desk, with vehicle details and the preferred time captured up front. Walk-in vehicles join the live queue in real time.',
      },
      {
        icon: 'Wrench',
        title: 'Repair Orders, Bays & Technicians',
        body: 'Open a repair order per vehicle with labour, parts and sublets, assign it to a technician, and watch progress on the technician board. Technicians clock on and off each job, and quality control signs the car out with a gate pass.',
      },
      {
        icon: 'Boxes',
        title: 'Parts, Purchasing & GRN',
        body: 'Order from suppliers with purchase orders, receive deliveries in full or in part against them, and see landed cost on every line. Batch, serial and tyre DOT tracking, low-stock alerts and stock take keep the shelves honest.',
      },
      {
        icon: 'ShieldCheck',
        title: 'Warranty, Body Shop & Insurance',
        body: 'Track warranty claims through to settlement and keep the failed parts with the claim. Body shop estimates go to the insurer, and the job bills the insurer and the customer separately from one record.',
      },
      {
        icon: 'Landmark',
        title: 'Invoicing, UAE VAT & Finance',
        body: 'Issue itemised invoices for labour, parts and consumables with UAE VAT or India GST. Every invoice, bill and stock movement posts its own journal, so bank reconciliation, VAT 201 returns and financial statements come from the same data.',
      },
      {
        icon: 'Bot',
        title: 'AI Assistant & Customer Updates',
        body: 'Confirmations, progress notes and ready-for-collection alerts go out by SMS and WhatsApp automatically. The AI Assistant answers questions about services, prices and open slots on your own WhatsApp number, 24x7, and passes anything complex to your team.',
      },
    ],
    highlights: CAR_HIGHLIGHTS,
    showcase: CAR_SHOWCASE,
    benefits: [
      'Keep every bay and technician busy with real-time job and clocking visibility',
      'Get warranty claims paid with failed parts and paperwork kept together',
      'Know what you ordered, what arrived and what it cost with PO and GRN',
      'Close the month faster with automatic journals and ready VAT 201 returns',
      'Win after-hours enquiries with an assistant that replies in seconds',
      'Bring customers back with service reminders, recalls and satisfaction surveys',
      'Bill insurers and customers separately without re-keying the job',
      'Make staffing and capacity decisions with built-in workshop analytics',
    ],
  },
  {
    slug: 'education-services',
    industry: 'Education Services',
    tagline: 'Sports Coaching & Coaching Centres',
    services: ['Sports Coaching', 'Coaching Centre'],
    heroSubtitle:
      'Organise your sessions, manage coaches, collect fees on time, and grow your coaching business — so you can focus on what you do best.',
    challenge:
      'Coaching businesses depend on consistent scheduling, high student attendance, and reliable revenue collection. Managing batch timetables, one-on-one session bookings, coach availability, and fee collection manually leads to missed sessions, scheduling conflicts, delayed payments, and the administrative burden of chasing parents or students for confirmations.',
    solutions: [
      {
        icon: 'Calendar',
        title: 'Session & Batch Scheduling',
        body: 'Set up recurring batch schedules, one-on-one coaching slots, or workshop dates with ease. Clients book available sessions online, via WhatsApp, or through the centre directly. All bookings flow into a unified calendar, eliminating double-bookings and timetable conflicts.',
      },
      {
        icon: 'Users',
        title: 'Coach & Resource Management',
        body: 'Manage multiple coaches, trainers, or tutors — each with their own schedule, subject specialisation, and availability. Assign students to the right coach, track sessions per coach, and manage venue or court allocations alongside staff scheduling.',
      },
      {
        icon: 'MessageSquare',
        title: 'Automated Attendance Reminders',
        body: 'Reduce absenteeism and last-minute drop-outs by sending automated session reminders to students or parents via SMS and WhatsApp. For individual coaching, reminders ensure both the coach and client are prepared and on time.',
      },
      {
        icon: 'FileText',
        title: 'Fee Collection & Invoicing',
        body: 'Issue invoices for session fees, monthly subscriptions, batch enrolments, or workshop registrations — with UAE VAT and India GST compliance. Track payment status, send payment reminders, and maintain a complete fee collection history per student.',
      },
      {
        icon: 'BarChart3',
        title: 'Analytics & Performance Insights',
        body: 'Track enrolment numbers, attendance rates, revenue per coach or batch, and peak booking periods. Understand which programmes are growing and which need attention — and make evidence-based decisions to scale your coaching business.',
      },
    ],
    benefits: [
      'Organise complex timetables and coach schedules without conflicts',
      'Improve attendance with automated session reminders',
      'Simplify fee collection with digital, tax-compliant invoicing',
      'Maintain records of every student\'s session and payment history',
      'Scale across multiple coaches, programmes, and locations',
    ],
  },
  {
    slug: 'hospitality',
    industry: 'Hospitality',
    tagline: 'Venue Booking & Event Spaces',
    services: ['Venue Booking'],
    heroSubtitle:
      'Eliminate double-bookings, coordinate event teams seamlessly, and deliver an experience that turns first-time clients into repeat customers.',
    challenge:
      'Venue operators deal with high-stakes bookings where a double-booking or a missed communication can be catastrophic. Managing enquiries across multiple channels, confirming availability in real time, coordinating setup crews, and collecting advance deposits and final payments in a structured, traceable way is critical — yet most venues still rely on manual registers, phone confirmations, and shared spreadsheets.',
    solutions: [
      {
        icon: 'Calendar',
        title: 'Real-Time Venue Booking & Availability',
        body: 'Display live venue availability online and accept bookings directly through your booking link, WhatsApp, or in person. Customers see available dates and time slots in real time — eliminating back-and-forth to check availability. Every confirmed booking is reflected instantly, preventing double-bookings.',
      },
      {
        icon: 'Users',
        title: 'Crew & Setup Team Management',
        body: 'Coordinate your catering team, event setup crew, AV technicians, security, and cleaning staff around each booking. Assign resources per event, manage shift timings, and ensure every team member knows their role well before the event day.',
      },
      {
        icon: 'MessageSquare',
        title: 'Automated Booking Confirmations & Reminders',
        body: 'Send automated booking confirmations, pre-event checklists, and day-before reminders to clients via SMS and WhatsApp. Internal team notifications ensure your crew is briefed and ready. Professional communication builds client confidence.',
      },
      {
        icon: 'FileText',
        title: 'Advance Deposits, Invoicing & Payment Tracking',
        body: 'Issue structured invoices covering advance deposits, venue hire fees, catering packages, and additional services — with UAE VAT and India GST compliance. Track payment milestones, send payment reminders, and maintain a complete financial record per event.',
      },
      {
        icon: 'BarChart3',
        title: 'Analytics & Business Insights',
        body: 'Understand your venue\'s utilisation rate, peak booking periods, revenue by event type, and client repeat rates. Identify which packages are most popular and which time slots are chronically underbooked. Use data to optimise pricing.',
      },
    ],
    benefits: [
      'Eliminate double-bookings with real-time availability and instant confirmation',
      'Coordinate event teams and resources without manual briefing overhead',
      'Reduce payment disputes with transparent, milestone-based digital invoicing',
      'Build a professional client experience from enquiry to post-event follow-up',
      'Fill more dates by understanding demand patterns and peak-season trends',
    ],
  },
];
